import { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import { collection } from '@app/firebase/firestore'
import { formatDateForUI } from '@app/libs/formatters'
import { CircularProgress, Typography, Paper } from '@mui/material'
import { useCurrentUser } from '@app/context/CurrentUser'

// Helper to format currency values
const formatCurrency = (value) => {
  return isNaN(parseFloat(value))
    ? '---'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        parseFloat(value),
      )
}

// Format a claim object with common processing logic
const formatClaim = (data, source, id) => {
  const {
    cptCodes = [], // Default to empty array if cptCodes is undefined
    cptCode = null,
    name: treatment = '',
    price = '',
    appointmentDate = '',
    date_of_service_initial: externalDate = '',
    original_charge: externalPrice = '',
  } = data.service || data.service_info || {}

  // Process cptCodes - if empty or undefined, check for cptCode
  const processedCptCodes =
    Array.isArray(cptCodes) && cptCodes.length > 0
      ? cptCodes.join(' ')
      : cptCode
        ? [cptCode]
        : '---'

  const { name: clinicName = '---', providerName = '' } = data.organization || {}

  const { name: externalProviderName = '---' } = data.provider_info || {}

  return {
    id: id,
    cpt: processedCptCodes || '---',
    treatment: treatment || '---',
    billed: formatCurrency(source === 'Internal' ? price : externalPrice),
    date: formatDateForUI(source === 'Internal' ? appointmentDate : externalDate) || 'Pending',
    clinicName: clinicName || '---',
    providerName: source === 'Internal' ? providerName || '---' : externalProviderName || '---',
    source,
  }
}

const getClaims = async (planID) => {
  if (!planID) return []

  try {
    // Get internal claims
    const claimsSnapshot = await collection('vouchers').where('plan.id', '==', planID).get()
    const internalClaims = claimsSnapshot.docs.map((doc) =>
      formatClaim(doc.data(), 'Internal', doc.id),
    )

    // Get external claims
    const externalSnapshot = await collection('external-claims')
      .where('plan_info.id', '==', planID)
      .get()
    const externalClaims = externalSnapshot.docs.map((doc) =>
      formatClaim(doc.data(), 'External', doc.data().DOCUMENT_NUM || `missing-doc-num-${doc.id}`),
    )

    return [...internalClaims, ...externalClaims]
  } catch (error) {
    console.error('Error fetching claims:', error)
    throw error
  }
}

// Column definitions
const columns = [
  { field: 'id', headerName: 'ID', width: 200 },
  { field: 'cpt', headerName: 'CPT Codes', width: 150 },
  { field: 'treatment', headerName: 'Treatment', width: 150 },
  { field: 'billed', headerName: 'Billed', width: 150 },
  { field: 'date', headerName: 'Date', width: 110 },
  { field: 'clinicName', headerName: 'Clinic Name', width: 160 },
  { field: 'providerName', headerName: 'Provider Name', width: 160 },
  { field: 'source', headerName: 'Source', width: 160 },
]

const ClaimsActivity = () => {
  const { managedPlans } = useCurrentUser()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!managedPlans?.managedPlanID) {
      setRows([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(false)

    getClaims(managedPlans.managedPlanID)
      .then(setRows)
      .catch((err) => {
        console.error('Error getting claims:', err)
        setError('We encountered an error while fetching claims.')
      })
      .finally(() => setLoading(false))
  }, [managedPlans])

  // Custom component for "no rows" state
  const NoRowsOverlay = () => (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      height='100%'
      p={2}
    >
      <Typography variant='h6' color='text.secondary' gutterBottom>
        No Claims Found
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        There are no claims available for this plan at this time.
      </Typography>
    </Box>
  )

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
        <Typography color='error'>{error}</Typography>
      </Box>
    )
  }

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 5 },
        },
        filter: {
          filterModel: {
            items: [],
            quickFilterExcludeHiddenColumns: true,
          },
        },
      }}
      pageSizeOptions={[5, 10, 25, 50, 100]}
      checkboxSelection
      slots={{
        toolbar: GridToolbar,
        noRowsOverlay: NoRowsOverlay,
      }}
      slotProps={{
        toolbar: {
          showQuickFilter: true,
        },
      }}
      sx={{ minHeight: '200px' }}
    />
  )
}

export default ClaimsActivity
